/**
 * Theme Provider Component
 * Provides theme context and theme switching functionality
 *
 * Features:
 * - Runtime theme switching (light/dark/system)
 * - Theme persistence in localStorage
 * - System preference detection
 * - Smooth theme transitions
 * - Theme-aware React context
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';

// ============================================================================
// Types & Interfaces
// ============================================================================

export type ThemeMode = 'light' | 'dark' | 'system';

export interface ThemeContextValue {
  /** Current theme mode */
  theme: ThemeMode;
  /** Resolved theme (light or dark) */
  resolvedTheme: 'light' | 'dark';
  /** Set theme mode */
  setTheme: (theme: ThemeMode) => void;
  /** Toggle between light and dark */
  toggleTheme: () => void;
  /** Check if system preference is dark */
  systemTheme: 'light' | 'dark';
  /** Whether the theme is ready (hydrated from storage) */
  isThemeReady: boolean;
}

// ============================================================================
// Theme Context
// ============================================================================

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// ============================================================================
// Theme Provider Component
// ============================================================================

interface ThemeProviderProps {
  children: React.ReactNode;
  /** Default theme if no preference is stored */
  defaultTheme?: ThemeMode;
  /** Storage key for theme preference */
  storageKey?: string;
  /** Enable system theme detection */
  enableSystem?: boolean;
  /** Enable smooth transitions */
  enableTransitions?: boolean;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'system',
  storageKey = 'theme',
  enableSystem = true,
  enableTransitions = true,
}) => {
  // State for theme management
  const [theme, setThemeState] = useState<ThemeMode>(defaultTheme);
  const isThemeReady = true; // Always ready to avoid blocking app initialization

  // Detect system theme preference
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
    return 'light';
  });

  // Resolve current theme based on mode and system preference
  const resolvedTheme: 'light' | 'dark' =
    theme === 'system' ? systemTheme : theme;

  // ============================================================================
  // Theme Management Functions
  // ============================================================================

  // Set theme with storage persistence
  const setTheme = useCallback(
    (newTheme: ThemeMode) => {
      setThemeState(newTheme);

      // Persist to localStorage
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(storageKey, newTheme);
        } catch (error) {
          console.warn('Failed to save theme preference:', error);
        }
      }
    },
    [storageKey],
  );

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    const newTheme = resolvedTheme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  }, [resolvedTheme, setTheme]);

  // ============================================================================
  // System Theme Detection
  // ============================================================================

  useEffect(() => {
    if (!enableSystem) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    // Listen for system theme changes
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [enableSystem]);

  // ============================================================================
  // DOM Theme Application
  // ============================================================================

  useEffect(() => {
    const root = document.documentElement;

    // Apply theme to DOM
    if (resolvedTheme === 'dark') {
      root.setAttribute('data-theme', 'dark');
      root.style.setProperty('color-scheme', 'dark');
    } else {
      root.setAttribute('data-theme', 'light');
      root.style.setProperty('color-scheme', 'light');
    }

    // Add transition class for smooth changes
    if (enableTransitions) {
      root.style.setProperty('transition', 'var(--theme-transition)');
      root.classList.add('theme-transitioning');

      const timeoutId = setTimeout(() => {
        root.classList.remove('theme-transitioning');
        root.style.removeProperty('transition');
      }, 300);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [resolvedTheme, enableTransitions]);

  // ============================================================================
  // Hydration and Storage
  // ============================================================================

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      // Load theme from localStorage
      const storedTheme = window.localStorage.getItem(storageKey) as ThemeMode;
      if (storedTheme && ['light', 'dark', 'system'].includes(storedTheme)) {
        setThemeState(storedTheme);
      }
    } catch (error) {
      console.warn('Failed to load theme preference:', error);
    }
  }, [storageKey]);

  // Note: We don't hide children during theme initialization anymore
  // to avoid conflicts with App's own loading states

  const contextValue: ThemeContextValue = {
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
    systemTheme,
    isThemeReady,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// ============================================================================
// Theme Hook
// ============================================================================

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// ============================================================================
// Theme Utilities
// ============================================================================

export const themeUtils = {
  /** Check if current theme is dark */
  isDark: (theme: ThemeMode, systemTheme: 'light' | 'dark'): boolean => {
    return theme === 'dark' || (theme === 'system' && systemTheme === 'dark');
  },

  /** Get theme-aware color */
  getThemeColor: (
    lightColor: string,
    darkColor: string,
    currentTheme: 'light' | 'dark',
  ): string => {
    return currentTheme === 'dark' ? darkColor : lightColor;
  },

  /** Apply theme to CSS custom properties */
  applyThemeColors: (
    _theme: 'light' | 'dark',
    colors: Record<string, string>,
  ) => {
    const root = document.documentElement;
    Object.entries(colors).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
  },
};
